import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Field } from './Modal';
import { Chip } from './StatusChip';
import ErrorState from './ErrorState';
import { useConfirm } from './ConfirmDialog';
import { apiFetch, apiErrorMessage } from '../hooks/useApi';

/**
 * <ClientScaleEditor /> — one client scale (risk / residual risk / priority).
 *
 * Shows the inheritance state explicitly, which is the whole point of the
 * client-config model: either "using the CloudSafe default" with a Customise
 * CTA, or the client's own scale with a Reset option.
 *
 * Saving supersedes the scale rather than editing it, so forms already
 * submitted keep rendering under the version they were captured with. The
 * warning below says so, because users should not be surprised by that.
 */
// Level colours carry a name so the choice is never colour-only.
const SWATCHES = [['#b91c1c', 'Red'], ['#b45309', 'Amber'], ['#1d4ed8', 'Blue'], ['#0f766e', 'Teal'], ['#6d28d9', 'Purple'], ['#be185d', 'Pink'], ['#4b5563', 'Grey']];
const colourName = (c) => (SWATCHES.find(([hex]) => hex.toLowerCase() === String(c || '').toLowerCase()) || [null, 'Custom'])[1];

const blankLevel = () => ({ code: '', label: '', description: '', colour: SWATCHES[0][0], sla_days: '' });

const ClientScaleEditor = ({ clientId, kind, title, blurb, withSla = false }) => {
  const [scale, setScale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [levels, setLevels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [initial, setInitial] = useState('');
  const { confirm } = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiFetch(`/api/contractor/clients/${clientId}/scales/${kind}`);
      setScale(d?.scale || null);
      setError(null);
    } catch (e) { setError(e); }
    finally { setLoading(false); }
  }, [clientId, kind]);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    setName(scale?.name || '');
    // Seed from whatever is in force — customising the system default should
    // start from its levels rather than a blank slate.
    const seeded = (scale?.levels || []).map((l) => ({
      code: l.code || '', label: l.label || '', description: l.description || '',
      colour: l.colour || SWATCHES[0][0], sla_days: l.sla_days ?? '',
    }));
    setLevels(seeded);
    setInitial(JSON.stringify({ name: scale?.name || '', levels: seeded }));
    setFormError(null);
    setEditing(true);
  };
  const cancelEdit = async () => {
    const dirty = JSON.stringify({ name, levels }) !== initial;
    if (dirty && !(await confirm({ title: 'Discard changes to this scale?', body: "Your edits haven't been saved.", confirmLabel: 'Discard changes', tone: 'danger' }))) return;
    setEditing(false);
  };

  const setLevel = (i, patch) => setLevels((ls) => ls.map((l, x) => (x === i ? { ...l, ...patch } : l)));
  const addLevel = () => setLevels((ls) => [...ls, blankLevel()]);
  const removeLevel = (i) => setLevels((ls) => ls.filter((_, x) => x !== i));
  const move = (i, dir) => setLevels((ls) => {
    const next = [...ls]; const j = i + dir;
    if (j < 0 || j >= next.length) return ls;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  const save = async () => {
    if (!levels.length) { setFormError('Add at least one level'); return; }
    const missing = levels.findIndex((l) => !String(l.code).trim());
    if (missing >= 0) { setFormError(`Level ${missing + 1} needs a code`); return; }
    const codes = levels.map((l) => String(l.code).trim().toLowerCase());
    if (new Set(codes).size !== codes.length) { setFormError('Each level needs its own code'); return; }
    setFormError(null);
    setSaving(true);
    try {
      const d = await apiFetch(`/api/contractor/clients/${clientId}/scales/${kind}`, {
        method: 'PUT', body: JSON.stringify({ name, levels }),
      });
      setScale(d?.scale || null);
      setEditing(false);
      toast.success(`${title} saved`);
    } catch (e) { setFormError(apiErrorMessage(e, "Couldn't save the scale")); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    const ok = await confirm({
      title: `Reset the ${String(title).toLowerCase()} to the CloudSafe default?`,
      body: 'Future forms for this client use the default scale. Submissions already made keep the scale they were captured under.',
      confirmLabel: 'Reset to default', tone: 'danger',
    });
    if (!ok) return;
    try {
      const d = await apiFetch(`/api/contractor/clients/${clientId}/scales/${kind}`, { method: 'DELETE' });
      setScale(d?.scale || null);
      toast.success(`${title} reset to the CloudSafe default`);
    } catch (e) { toast.error(`Couldn't reset the scale: ${apiErrorMessage(e)}`); }
  };

  const isCustom = scale?.source === 'client';

  return (
    <section style={{ background: 'var(--cw-surface)', border: '1px solid var(--cw-border)', borderRadius: 10, padding: 18 }} aria-label={title}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ fontWeight: 700, color: 'var(--cw-text)', margin: 0, fontSize: 'var(--cs-text-md)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {title}
            <Chip tone={isCustom ? 'info' : 'neutral'} label={isCustom ? `Custom · version ${scale.version}` : 'CloudSafe default'} />
          </h3>
          {blurb && <p className="cw-meta" style={{ margin: '3px 0 0' }}>{blurb}</p>}
        </div>
        {!editing && !error && (
          <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
            {isCustom && <button type="button" className="cw-btn cw-btn-secondary" onClick={reset}>Reset to default</button>}
            <button type="button" className="cw-btn cw-btn-primary" onClick={startEdit} disabled={loading}>
              <i className="fa-solid fa-sliders" aria-hidden="true" /> {isCustom ? 'Edit scale' : 'Customise for this client'}
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div role="status" className="cw-meta" style={{ padding: '20px 0' }}>Loading…</div>
      ) : error ? (
        <div style={{ marginTop: 12 }}><ErrorState compact thing={String(title).toLowerCase()} error={error} onRetry={load} /></div>
      ) : !editing ? (
        <ul style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14, padding: 0, listStyle: 'none' }}>
          {(scale?.levels || []).map((l) => (
            <li key={l.position ?? l.code}
              style={{ display: 'flex', alignItems: 'center', gap: 7, border: '1px solid var(--cw-border)', borderRadius: 8, padding: '6px 10px' }}>
              <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 3, background: l.colour || 'var(--cw-placeholder)', flexShrink: 0 }} />
              <span style={{ fontWeight: 700, color: 'var(--cw-text)', fontSize: 'var(--cs-text-sm)' }}>{l.code}</span>
              {l.label && <span style={{ color: 'var(--cw-text-muted)', fontSize: 'var(--cs-text-sm)' }}>{l.label}</span>}
              {withSla && l.sla_days != null && <span className="cw-meta">· {l.sla_days} day SLA</span>}
              {l.description && <span className="cw-sr-only">— {l.description}</span>}
            </li>
          ))}
        </ul>
      ) : (
        <div style={{ marginTop: 16 }}>
          {formError && <div className="cw-error-state compact" role="alert" style={{ marginBottom: 12 }}><div className="cw-error-state-text"><p className="cw-error-state-title">Not saved</p><p className="cw-error-state-reason">{formError}</p></div></div>}
          <div style={{ maxWidth: 320, marginBottom: 14 }}>
            <Field label="Scale name">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Client A–E" />
            </Field>
          </div>

          <ol style={{ display: 'grid', gap: 10, listStyle: 'none', margin: 0, padding: 0 }}>
            {levels.map((l, i) => (
              <li key={i}>
                <fieldset style={{ border: '1px solid var(--cw-border-light)', borderRadius: 8, padding: 10, margin: 0, display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <legend className="cw-label" style={{ padding: '0 4px' }}>Level {i + 1}</legend>
                  <div style={{ display: 'flex', gap: 2 }}>
                    <button type="button" className="cw-icon-btn" aria-label={`Move level ${i + 1} up`} onClick={() => move(i, -1)} disabled={i === 0}><i className="fa-solid fa-chevron-up" aria-hidden="true" /></button>
                    <button type="button" className="cw-icon-btn" aria-label={`Move level ${i + 1} down`} onClick={() => move(i, 1)} disabled={i === levels.length - 1}><i className="fa-solid fa-chevron-down" aria-hidden="true" /></button>
                  </div>
                  <Field label="Code" required style={{ flex: '0 1 90px', margin: 0 }}>
                    <input style={{ fontWeight: 700 }} value={l.code} onChange={(e) => setLevel(i, { code: e.target.value })} />
                  </Field>
                  <Field label="Label" style={{ flex: '1 1 140px', margin: 0 }}>
                    <input value={l.label} onChange={(e) => setLevel(i, { label: e.target.value })} />
                  </Field>
                  <Field label="What it means" style={{ flex: '2 1 200px', margin: 0 }}>
                    <input value={l.description} onChange={(e) => setLevel(i, { description: e.target.value })} />
                  </Field>
                  {withSla && (
                    <Field label="SLA (days)" style={{ flex: '0 1 96px', margin: 0 }}>
                      <input value={l.sla_days} inputMode="numeric" onChange={(e) => setLevel(i, { sla_days: e.target.value.replace(/[^0-9]/g, '') })} />
                    </Field>
                  )}
                  <Field label="Colour" style={{ flex: '0 1 130px', margin: 0 }}>
                    <select value={l.colour} onChange={(e) => setLevel(i, { colour: e.target.value })} style={{ paddingLeft: 26, backgroundImage: `radial-gradient(circle at 12px 50%, ${l.colour} 5px, transparent 5.5px)`, backgroundRepeat: "no-repeat" }}>
                      {SWATCHES.map(([hex, nm]) => <option key={hex} value={hex}>{nm}</option>)}
                      {!SWATCHES.some(([hex]) => hex.toLowerCase() === String(l.colour).toLowerCase()) && <option value={l.colour}>{colourName(l.colour)}</option>}
                    </select>
                  </Field>
                  <button type="button" className="cw-icon-btn" aria-label={`Remove level ${i + 1}${l.code ? ` (${l.code})` : ''}`} title="Remove level" onClick={() => removeLevel(i)} style={{ color: 'var(--cw-danger)' }}>
                    <i className="fa-solid fa-trash" aria-hidden="true" />
                  </button>
                </fieldset>
              </li>
            ))}
          </ol>

          <button type="button" className="cw-btn cw-btn-secondary" style={{ marginTop: 12 }} onClick={addLevel}>
            <i className="fa-solid fa-plus" aria-hidden="true" /> Add level
          </button>

          <p role="note" style={{ marginTop: 14, fontSize: 'var(--cs-text-sm)', color: 'var(--cw-warning-fg)', background: 'var(--cw-warning-tint)', border: '1px solid var(--cw-warning-border)', borderRadius: 8, padding: '9px 11px', lineHeight: 1.45 }}>
            <i className="fa-solid fa-circle-info" style={{ marginRight: 6 }} aria-hidden="true" />
            This affects all future forms submitted on jobs for this client. Submissions already made keep
            the scale they were captured under, so historic reports never change meaning.
          </p>

          <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
            <button type="button" className="cw-btn cw-btn-secondary" onClick={cancelEdit} disabled={saving}>Cancel</button>
            <button type="button" className="cw-btn cw-btn-primary" onClick={save} disabled={saving} aria-busy={saving || undefined}>
              {saving ? 'Saving…' : 'Save scale'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default ClientScaleEditor;
